import { useNavigate } from '@tanstack/react-router';
import { useLogout } from '@/hooks/core/useAuthQuery';

interface SessionRecoveryAlertProps {
  centered?: boolean;
}

/**
 * UIUX-05: the recovery screen must never trap a user. The old copy blamed the
 * connection and offered only reload, but the usual cause is an orphaned or
 * expired token - so this screen offers an honest diagnosis and a working
 * logout/login escape alongside reload.
 */
export function SessionRecoveryAlert({ centered = false }: SessionRecoveryAlertProps) {
  const navigate = useNavigate();
  const logout = useLogout();

  // The escape must not depend on the backend accepting the logout (an
  // unreachable backend is one cause of this screen): useLogout purges local
  // auth state in its onSettled, so navigating to /login unconditionally is
  // safe either way.
  const handleLogout = () => {
    logout.mutate(undefined, {
      onSettled: () => {
        void navigate({ to: '/login' });
      },
    });
  };

  return (
    <div
      role="alert"
      style={
        centered
          ? { minHeight: '100dvh', display: 'grid', placeItems: 'center', padding: 24 }
          : undefined
      }
    >
      <div style={centered ? { maxWidth: 360, textAlign: 'center' } : undefined}>
        <h1 style={centered ? { fontSize: 20 } : undefined}>We could not verify your session</h1>
        <p>
          This is usually not a connection problem - your login may have expired on this device. Try
          again, or log out and sign in again.
        </p>
        <button type="button" onClick={() => window.location.reload()}>
          Try again
        </button>{' '}
        <button type="button" onClick={handleLogout} disabled={logout.isPending}>
          {logout.isPending ? 'Logging out...' : 'Log out and sign in again'}
        </button>
      </div>
    </div>
  );
}
